import React, { useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Grid,
  Autocomplete,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

/**
 * A component for rendering and editing a date-text item
 */
const SelectItem = ({
  id,
  description,
  date,
  textValue,
  config,
  completed = false,
  onUpdate,
  onDelete,
  editable = true,
  draggable = true,
  isTemplateView = false,
}) => {
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [itemDescription, setItemDescription] = useState(description || "");
  const [itemTextValue, setItemTextValue] = useState(textValue || "");
  const [itemCompletionDate, setItemCompletionDate] = useState(date || "");
  // const [dateValue, setDateValue] = useState("")
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDescriptionChange = (e) => {
    setItemDescription(e.target.value);
  };

  const handleDescriptionBlur = () => {
    setIsEditingDescription(false);
    if (itemDescription.trim() !== description) {
      onUpdate(id, { description: itemDescription.trim() });
    }
  };

  const handleTextValueChange = (e, newValue) => {
    console.log(newValue);
    setItemTextValue(newValue);
    setItemCompletionDate(!newValue ? "" : dayjs().format("YYYY-MM-DD"));
  };

  const handleTextValueBlur = () => {
    setIsEditingText(false);
    if (itemTextValue !== textValue) {
      onUpdate(id, {
        textValue: itemTextValue,
        date: itemCompletionDate,
        completed: itemTextValue ? true : false,
      });
    }
  };

  const handleKeyDown = (e, field) => {
    if (e.key === "Enter") {
      if (field === "description") {
        handleDescriptionBlur();
      } else if (field === "textValue") {
        handleTextValueBlur();
      }
    }
  };

  const handleDelete = () => {
    onDelete(id);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 1,
          borderRadius: 1,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          "&:hover": {
            bgcolor: "action.hover",
          },
        }}
      >
        <Grid container spacing={2} columns={24}>
          {draggable && (
            <Grid size={{ xs: 1, sm: 1, md: 1 }}>
              <IconButton
                size="small"
                sx={{
                  cursor: "grab",
                  color: "text.disabled",
                  mr: 0.5,
                  mt: 0.5,
                  visibility: isMobile ? "hidden" : "visible",
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Grid>
          )}

          {/* Description Field */}
          <Grid container size={{ xs: 22, sm: 22, md: 22 }} columns={24}>
            <Grid size={{ xs: 24, sm: 24, md: 10 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                {isEditingDescription && editable && isTemplateView ? (
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Description"
                    value={itemDescription}
                    onChange={handleDescriptionChange}
                    onBlur={handleDescriptionBlur}
                    onKeyDown={(e) => handleKeyDown(e, "description")}
                    autoFocus
                    size="small"
                  />
                ) : (
                  <Box
                    onClick={() => editable && setIsEditingDescription(true)}
                    sx={{
                      cursor: editable ? "pointer" : "default",
                      wordBreak: "break-word",
                      fontWeight: 500,
                      lineHeight: "normal",
                      minHeight: "2.5em",
                      display: "flex",
                      alignItems: "center",
                      ml: 1.5,
                    }}
                  >
                    {description || "Click to add description..."}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid
              size={{ xs: 24, sm: 24, md: 5 }}
              justifyContent="center"
              alignItems="center"
            >
              <Box
                variant="body1"
                onClick={() => editable && setIsEditingDescription(true)}
                sx={{
                  cursor: editable ? "pointer" : "default",
                  wordBreak: "break-word",
                  fontWeight: 500,
                  lineHeight: "normal",
                  minHeight: "2.5em",
                  display: "flex",
                  alignItems: "center",
                  ml: isMobile ? 1.5 : 0,
                }}
              >
                {date || ""}
              </Box>
            </Grid>
            <Grid size={{ xs: 24, sm: 24, md: 9 }}>
              <Autocomplete
                sx={{
                  width: "100%",
                }}
                options={config.options}
                disabled={isTemplateView}
                value={itemTextValue || null}
                defaultValue={textValue}
                label={isTemplateView ? config.selectedList : ""}
                onChange={(event, newValue) => {
                  handleTextValueChange(event, newValue);
                }}
                onBlur={handleTextValueBlur}
                onKeyDown={(e) => handleKeyDown(e, "textValue")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    disabled={isTemplateView}
                    variant="outlined"
                    placeholder="Vælg"
                    label={isTemplateView ? config.selectedList : ""}
                    multiline={false}
                    sx={{
                      "& .MuiInputBase-root.Mui-disabled": {
                        backgroundColor: "#f5f5f5",
                      },
                      "& .MuiInputBase-root": {
                        maxHeight: "45.75px",
                        overflow: "clip",
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
          {editable && isTemplateView && (
            <Grid
              size={{ xs: 1, sm: 1, md: 1 }}
              sx={{ justifyContent: "right" }}
            >
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{ color: "error.light", mt: 0.5 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Paper>
    </LocalizationProvider>
  );
};

export default SelectItem;
